'use client';

import React, { createContext, useContext, type ReactNode } from 'react';
import { CookieManager, useCookieConsent } from 'react-cookie-manager';
import { useRegionCurrency } from '@skin-seoul/service';
import { doesRequireGDPRConsent } from '@/constants/gdpr';
import { useIsWebView } from '@/providers/IsWebviewProvider';
interface CookieConsentProviderProps {
  children: ReactNode;
}
interface CookieConsentStatus {
  hasFullConsent: boolean;
  hasAnalyticsConsent: boolean;
  hasAdvertisingConsent: boolean;
  isDeclined: boolean;
  requiresConsent: boolean;
}

// Context for consent status - defaults to NO consent for safety
const CookieConsentStatusContext = createContext<CookieConsentStatus>({
  hasFullConsent: false,
  hasAnalyticsConsent: false,
  hasAdvertisingConsent: false,
  isDeclined: false,
  requiresConsent: true
});

/**
 * Inner component that provides consent status for EU users
 * Must be inside CookieManager to use useCookieConsent
 */
function EUConsentStatusProvider({
  children
}: {
  children: ReactNode;
}) {
  const {
    hasConsent,
    isDeclined
  } = useCookieConsent();

  // Since we only have Accept All / Decline All, all categories share the same consent status
  const consentGiven = hasConsent === true;
  const status: CookieConsentStatus = {
    hasFullConsent: consentGiven,
    hasAnalyticsConsent: consentGiven,
    hasAdvertisingConsent: consentGiven,
    isDeclined: Boolean(isDeclined),
    requiresConsent: true
  };
  return <CookieConsentStatusContext.Provider value={status} data-sentry-element="CookieConsentStatusContext.Provider" data-sentry-component="EUConsentStatusProvider" data-sentry-source-file="CookieConsentProvider.tsx">{children}</CookieConsentStatusContext.Provider>;
}

/**
 * Provides cookie consent management for GDPR compliance
 * Only shows consent banner for EU countries (EUR currency) in web browser
 * Skips consent banner in React Native webview (native app handles permissions)
 */
export function CookieConsentProvider({
  children
}: CookieConsentProviderProps) {
  const {
    country
  } = useRegionCurrency();
  const isWebView = useIsWebView();
  const showConsentBanner = !isWebView && doesRequireGDPRConsent(country);

  // For non-EU users, provide context with full consent immediately
  if (!showConsentBanner) {
    const nonEUConsent: CookieConsentStatus = {
      hasFullConsent: true,
      hasAnalyticsConsent: true,
      hasAdvertisingConsent: true,
      isDeclined: false,
      requiresConsent: false
    };
    return <CookieConsentStatusContext.Provider value={nonEUConsent}>{children}</CookieConsentStatusContext.Provider>;
  }

  // For EU users, render CookieManager with simple accept/decline popup
  return <CookieManager disableGeolocation={true} showManageButton={false} displayType="popup" theme="light" disableAutomaticBlocking={true} translations={{
    title: 'Cookie Preferences 🍪',
    message: 'We use cookies to enhance your browsing experience and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
    buttonText: 'Accept All',
    declineButtonText: 'Decline All'
  }} data-sentry-element="CookieManager" data-sentry-component="CookieConsentProvider" data-sentry-source-file="CookieConsentProvider.tsx">
      <EUConsentStatusProvider data-sentry-element="EUConsentStatusProvider" data-sentry-source-file="CookieConsentProvider.tsx">{children}</EUConsentStatusProvider>
    </CookieManager>;
}

/**
 * Hook to check if user has given consent for specific cookie categories
 * Use this to conditionally load tracking scripts
 * Safe to use anywhere - handles both EU and non-EU cases
 */
export function useCookieConsentStatus(): CookieConsentStatus {
  return useContext(CookieConsentStatusContext);
}